import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MessageService } from 'primeng/api';
import { CommunicationService } from 'src/app/services/communication/communication.service';
import { ContactServiceInfoResponse, itemDescription, TennatAccount, TennatAccountContactInfo } from '../../../../../express-tRPC/src/shared/types';
import moment from 'moment';
import { CommonSharedService } from 'src/app/shared/services/common-shared.service';
import { TabViewChangeEvent } from 'primeng/tabview';

export interface BillingSummaryItem {
  item: string;
  qty: number;
  amount: number;
}

export interface BillingSummaryItemWithDetails extends BillingSummaryItem {
  contacts: TennatAccountContactInfo[];
}

@Component({
  selector: 'app-monthly-billing-summary-widget',
  templateUrl: './monthly-billing-summary-widget.component.html',
  styleUrls: ['./monthly-billing-summary-widget.component.scss'],
})
export class MonthlyBillingSummaryWidgetComponent implements OnInit, OnChanges {

  @Input() location!: string;
  @Input() suite!: string;
  @Input() account!: string;
  @Input() refreshSummary!: boolean;

  loading = false;
  itemsSummary: BillingSummaryItem[]  = [];
  itemsTotal: number = 0;
  itemsSummaryNextMonth: BillingSummaryItem[] = [];
  itemsTotalNextMonth: number = 0;

  tenantContacts: TennatAccountContactInfo[] = [];
  terminatedTenantContacts: TennatAccountContactInfo[] = [];
  tenantContactServiceInfo: ContactServiceInfoResponse | undefined;

  currentMonthContactsByItem: Map<string, TennatAccountContactInfo[]> = new Map();
  nextMonthContactsByItem: Map<string, TennatAccountContactInfo[]> = new Map();

  accountDetails: TennatAccount | undefined = undefined;

  constructor(private messageService: MessageService, private commService: CommunicationService, private commonSharedService: CommonSharedService) { }

  async ngOnInit() {
    this.loading = true;
    await this.loadDescriptionLists();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['refreshSummary'].currentValue) {
      this.ngOnInit();
    }
  }

  async loadDescriptionLists() {

    try {
      this.tenantContactServiceInfo = await this.commService.getTenantContactServiceInfo();
      this.loadTenantInfo();
    } catch (error: any) {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
      this.loading = false;
    }
  }

  async loadTenantInfo() {
    try {
      let accountDetails: TennatAccount | void = await this.commService.getAccountDetails(this.suite, this.account)
      if (accountDetails) {
        // this.processBillingData(accountDetails);
        this.accountDetails = accountDetails;

        this.tenantContacts = structuredClone(this.accountDetails?.contactInfo || []);
        this.itemsSummary = this.buildMonthlySummary('current');
      }
    } catch (error: any) {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
    } finally {
      this.loading = false;
    }
  }

  tabChanged(event: TabViewChangeEvent) {

    this.tenantContacts = structuredClone(this.accountDetails?.contactInfo || []);

    if(event.index === 0) {
      this.itemsSummary = this.buildMonthlySummary('current');
    } else if(event.index === 1) {
      this.itemsSummaryNextMonth = this.buildMonthlySummary('next');
    }
  }
  
  /**
   * Processes tenant account details and generates
   * billing summaries for the current and next month.
   */
  processBillingData(accountDetailsResult: TennatAccount): void {

    this.tenantContacts = accountDetailsResult.contactInfo;

    this.itemsSummary = this.buildMonthlySummary('current');
    this.itemsSummaryNextMonth = this.buildMonthlySummary('next');

  }

  /**
   * Builds a monthly billing summary for a given type.
   * @param type "current" | "next" month
   * @returns Array of billing summary items
   */
  private buildMonthlySummary(type: "current" | "next"): BillingSummaryItem[] {
    //summaryMap is an object where the keys are strings and the values are BillingSummaryItem.
    const summaryMap: Record<string, BillingSummaryItem> = {};
    const contactsByItem = type === "current" ? this.currentMonthContactsByItem : this.nextMonthContactsByItem;

    // Clear previous data
    contactsByItem.clear();

    for (const contact of this.tenantContacts) {

      const isMatchingSureparkMemberIsActive = contact?.surepark_member_details?.active === true;

      //Consider contact as terminated if corsponding surepark member is INACTIVE OR NOT FOUND
      const isTerminated = this.isTerminated(contact, type) || !isMatchingSureparkMemberIsActive;

      if (!isTerminated) {
        this.enrichContactWithRate(contact);
      }

      const desc = this.resolveDescription(contact, isTerminated);
      const rate_amount = isTerminated ? '0' : contact.rate_amount;
      
      /**
       * Set this flag to false be default because 
       * to display `Billing Contact` tag in contact list this flag will be set to TRUE by API for first contact
       * Its only for calculation purpose so we have used this flag to calculate invoice amount
       * it wont effect BillingContact flag set by API
      **/
      contact.isBillingContact = false;

      //Lets set flag so we can filter if contact is billable or not
      if(desc != "Terminated" && desc != "Unassigned" && desc != "Can't be invoiced") {
        contact.isBillingContact = true;
      }

      // Store contact details by billing item
      if (!contactsByItem.has(desc)) {
        contactsByItem.set(desc, []);
      }
      contactsByItem.get(desc)!.push({...contact});

      this.aggregateSummary(summaryMap, desc, rate_amount);
    }

    const summaryArray = Object.values(summaryMap);

    // Calculate totals
    const total = summaryArray.reduce((sum, item) => sum + item.amount, 0);
    if (type === "current") {
      this.itemsTotal = total;
    } else {
      this.itemsTotalNextMonth = total;
    }

    return summaryArray;
  }

  /**
   * Determines if a tenant contact is terminated
   * relative to the current or next month.
   * @param contact Tenant contact info
   * @param type "current" | "next"
   */
  private isTerminated(
    contact: TennatAccountContactInfo,
    type: "current" | "next"
  ): boolean {
    if (!contact.terminationDates) return true;

    const terminationDate = moment.utc(contact.terminationDates);

    // console.log(`DEBUG terminateionDate ${terminationDate}`);

    if (type === "current") {
      return terminationDate.isBefore(moment.utc());
    }

    const firstDayNextMonth = moment.utc().add(1, "months").startOf("month");
    return terminationDate.isBefore(firstDayNextMonth);
  }

  /**
   * Enriches a tenant contact with rate description and amount
   * if a matching invoice item exists.
   * @param contact Tenant contact info
   */
  private enrichContactWithRate(contact: TennatAccountContactInfo) {
    const rate = this.tenantContactServiceInfo?.available_invoice_items.find(
      (item: itemDescription) => item.code === (contact.rate as unknown as string)
    );

    if (rate) {
      contact.rate_desc = rate.description;
      contact.rate_amount = String(
        this.commonSharedService.convertCurrencyStringToNumber(rate.unitprice)
      );
    }
  }

  /**
   * Resolves the description category for a tenant contact.
   * Categories: Terminated, Rate Description, Unassigned, Can't be invoiced
   * @param contact Tenant contact info
   * @param isTerminated Whether the contact is terminated
   */
  private resolveDescription(
    contact: TennatAccountContactInfo,
    isTerminated: boolean
  ): string {
    if (isTerminated) return "Terminated";
    if (contact.rate_desc) return contact.rate_desc;
    if (contact.rate_desc === undefined) return "Unassigned";
    return "Can't be invoiced";
  }

  /**
    * Aggregates a contact's billing data into the summary map.
    * @param summaryMap Record of billing summary items
    * @param desc Category/description
    * @param rateAmount Optional rate amount
    */

  private aggregateSummary(
    summaryMap: Record<string, BillingSummaryItem>,
    desc: string,
    rateAmount?: string
  ) {
    if (!summaryMap[desc]) {
      summaryMap[desc] = {
        item: desc,
        qty: 0,
        amount: 0,
      };
    }

    summaryMap[desc].qty += 1;
    summaryMap[desc].amount += parseFloat(rateAmount || "0");
  }

  /**
   * Filters tenant contacts into active and terminated lists
   * based on the termination date relative to today.
   */
  filterTenantContacts(): void {
    const now = moment.utc();

    this.terminatedTenantContacts = this.tenantContacts.filter((c) => {
      if (!c.terminationDates) {
        return false; // no termination date → not terminated
      }

      const terminationDate = moment.utc(c.terminationDates);
      return terminationDate.isBefore(now); // already ended
    });

    this.tenantContacts = this.tenantContacts.filter((c) => {
      if (!c.terminationDates) {
        return true; // no termination date → still active
      }

      const terminationDate = moment.utc(c.terminationDates);
      return terminationDate.isSameOrAfter(now); // today or future → active
    });
  }

  getBillableContacts(): TennatAccountContactInfo[] {
    return this.tenantContacts.filter((contact) => contact.isBillingContact);
  }
  getCurrentMonthBillingSummary(): BillingSummaryItem[] {
    return this.itemsSummary.filter((summary) => summary.item != "Terminated" && summary.item != "Unassigned" && summary.item != "Can't be invoiced");
  }

  /**
   * Gets the account details (contacts) for a specific billing item
   * @param itemName The name of the billing item
   * @param type "current" or "next" month
   * @returns Array of tenant account contact info for the item
   */
  getAccountDetailsForItem(itemName: string, type: "current" | "next"): TennatAccountContactInfo[] {
    const contactsByItem = type === "current" ? this.currentMonthContactsByItem : this.nextMonthContactsByItem;
    return contactsByItem.get(itemName) || [];
  }
}
